import type { AccountInfo } from "@azure/msal-browser";

/**
 * The mode of the authentication dialog.
 */
export enum DialogMode {
    SignIn = "signIn",
    SignOut = "signOut"
}

/**
 * Common fields shared by all dialog configurations.
 */
export interface DialogAuthConfigBase {
    mode: DialogMode
    msalConfigClientID: string;
    msalConfigAuthority: string;
}

/**
 * Configuration for sign-in mode, passed from the task pane to the dialog via messageChild.
 */
export interface DialogSignInConfig extends DialogAuthConfigBase {
    mode: DialogMode.SignIn;
    msalLoginScopes: string;
    msalTokenScopes: string;
    msalResourceID: string;
    oauthInitialLogin: string;
    signInDescription: string;
    signInButtonLabel: string;
    officeAppThemePrefix: string;
}

/**
 * Configuration for sign-out mode, passed from the task pane to the dialog via messageChild.
 */
export interface DialogSignOutConfig extends DialogAuthConfigBase {
    mode: DialogMode.SignOut;
    signingOutLabel: string;
}

/**
 * Configuration object passed from the task pane to the dialog via messageChild.
 */
export type DialogAuthConfig = DialogSignInConfig | DialogSignOutConfig;

/**
 * Messages sent from the dialog to the host via messageParent.
 */
export enum DialogMessageType {
    DialogReady = "dialogReady",
    Authenticated = "authenticated",
    OAuthError = "error",
    OAuthEvent = "event",
    SignedOut = "signedOut"
}

export interface DialogReadyMessage {
    messageType: DialogMessageType.DialogReady;
}

export interface DialogAuthenticatedMessage {
    messageType: DialogMessageType.Authenticated;
    accessToken: string;
    expiresOn: number;
    scopes: string[];
    account: AccountInfo;
}

export interface DialogOAuthErrorMessage {
    messageType: DialogMessageType.OAuthError;
    error: { errorMessage: string };
}

export interface DialogOAuthEventMessage {
    messageType: DialogMessageType.OAuthEvent;
    event: string;
}

export interface DialogSignedOutMessage {
    messageType: DialogMessageType.SignedOut;
}

export type DialogMessage =
    | DialogReadyMessage
    | DialogAuthenticatedMessage
    | DialogOAuthErrorMessage
    | DialogOAuthEventMessage
    | DialogSignedOutMessage;
